import type { TFunction } from 'i18next';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import checkMark from '@/assets/check_mark.svg';
import close from '@/assets/close.svg';
import descriptionIcon from '@/assets/description.svg';
import upload from '@/assets/upload.svg';
import warning from '@/assets/warning.svg';
import { ModalDialog, SpinnerOverlayView } from '@/components/Elements';
import {
  GLOSSARY_SAVE_RESULT,
  GlossarySaveResult,
} from '@/hooks/useGlossaryEntries';

import {
  GlossaryRow,
  INVALID_ROW_REASON,
  UPLOAD_WARNING_TYPE,
  UploadWarning,
  UploadWarningType,
  WarningRow,
} from '../constants';
import { useFileUpload } from '../hooks/useFileUpload';

import { GlossaryLangPanel } from './GlossaryLangPanel';
import styles from './GlossaryUploadDialog.module.scss';

// ---------------------------------------------------------------------------
// 定数
// ---------------------------------------------------------------------------

/**
 * 警告種別ごとの i18n キーマップ。
 * `Record<UploadWarningType, string>` により全種別の網羅を型で保証する。
 * キーは `t()` に直接渡すため typo はビルド時ではなく実行時に発覚するが、
 * 少なくとも「種別の追加漏れ」はコンパイルエラーで検出できる。
 */
/**
 * 用語集保存結果ごとの i18n キー・data-testid マップ。
 * 成功時(`succeeded`)は表示不要なため対象外。
 */
const SAVE_RESULT_MESSAGE: Record<
  Exclude<GlossarySaveResult, typeof GLOSSARY_SAVE_RESULT.SUCCEEDED>,
  { key: string; testId: string }
> = {
  [GLOSSARY_SAVE_RESULT.FAILED_QUOTA]: {
    key: 'glossary.saveFailedQuota',
    testId: 'glossary-save-failed-quota-message',
  },
  [GLOSSARY_SAVE_RESULT.FAILED_BLOCKED]: {
    key: 'glossary.saveFailedBlocked',
    testId: 'glossary-save-failed-blocked-message',
  },
  [GLOSSARY_SAVE_RESULT.TIMEOUT]: {
    key: 'glossary.saveTimeout',
    testId: 'glossary-save-timeout-message',
  },
};

const WARNING_TYPE_KEYS: Record<UploadWarningType, string> = {
  [UPLOAD_WARNING_TYPE.DUPLICATE]: 'glossary.warnDuplicate',
  [UPLOAD_WARNING_TYPE.MISSING_FIELD]: 'glossary.warnMissingField',
  [UPLOAD_WARNING_TYPE.OVERFLOW]: 'glossary.warnOverflow',
  [UPLOAD_WARNING_TYPE.UNSUPPORTED_LANG]: 'glossary.warnUnsupportedLang',
  [UPLOAD_WARNING_TYPE.INVALID_SOUNDS_LIKE_JPN]:
    'glossary.warnInvalidSoundsLikeJpn',
  [UPLOAD_WARNING_TYPE.INVALID_SOUNDS_LIKE_OTHER]:
    'glossary.warnInvalidSoundsLikeOther',
  [UPLOAD_WARNING_TYPE.TOO_MANY_WORDS_CONTENT]:
    'glossary.warnTooManyWordsPhrase',
  [UPLOAD_WARNING_TYPE.TOO_MANY_WORDS_SOUNDS_LIKE]:
    'glossary.warnTooManyWordsSoundsLike',
  [UPLOAD_WARNING_TYPE.CONTENT_TOO_LONG]: 'glossary.warnPhraseWordTooLong',
  [UPLOAD_WARNING_TYPE.SOUNDS_LIKE_TOO_LONG]:
    'glossary.warnSoundsLikeWordTooLong',
};

// ---------------------------------------------------------------------------
// ヘルパー
// ---------------------------------------------------------------------------

/**
 * 警告行の明細ラベルを返す。
 * - 読み方系（文字種違反 / 語数超過 / 文字数超過）: `言語: 語句 / 読み方: xxx`（問題のある読み方を明示）
 * - 空欄系: 欠損内容に応じたメッセージ
 * - その他（語句語数超過 / 語句文字数超過 / 重複 / 上限超過 等）: `言語: 語句`
 */
const resolveWarningRowLabel = (
  row: WarningRow,
  type: UploadWarningType,
  t: TFunction,
): string => {
  if (
    type === UPLOAD_WARNING_TYPE.INVALID_SOUNDS_LIKE_JPN ||
    type === UPLOAD_WARNING_TYPE.INVALID_SOUNDS_LIKE_OTHER ||
    type === UPLOAD_WARNING_TYPE.TOO_MANY_WORDS_SOUNDS_LIKE ||
    type === UPLOAD_WARNING_TYPE.SOUNDS_LIKE_TOO_LONG
  ) {
    return t('glossary.errorDetailSoundsLike', {
      language: row.language,
      content: row.content,
      soundsLike: row.soundsLike,
    });
  }

  if (type === UPLOAD_WARNING_TYPE.MISSING_FIELD) {
    switch (row.reason) {
      case INVALID_ROW_REASON.BOTH:
        return t('glossary.reasonBothMissing');
      case INVALID_ROW_REASON.NO_LANG:
        return t('glossary.reasonLanguageMissing', { content: row.content });
      case INVALID_ROW_REASON.NO_CONTENT:
        return t('glossary.reasonPhraseMissing', { language: row.language });
      case undefined:
        // MISSING_FIELD 警告で reason が未設定の場合（型上は許容されるが実装上は発生しない）
        // 空白よりは行の内容を示す
        return t('glossary.errorDetail', {
          language: row.language,
          content: row.content,
        });
      default: {
        // INVALID_ROW_REASON に値が追加された場合、コンパイルエラーで検出する
        const exhaustive: never = row.reason;

        return exhaustive;
      }
    }
  }

  return t('glossary.errorDetail', {
    language: row.language,
    content: row.content,
  });
};

// ---------------------------------------------------------------------------
// サブコンポーネント
// ---------------------------------------------------------------------------

/**
 * エラーなし・空CSV のとき表示する成功バナー
 */
const UploadResultBanner = ({ importCount }: { importCount: number }) => {
  const { t } = useTranslation();

  return (
    <div className={styles['no-error-banner']}>
      <div className={styles['no-error-banner-body']}>
        <span className={styles['no-error-banner-title']}>
          <img
            src={checkMark}
            alt=""
            className={styles['no-error-banner-icon']}
          />
          {t('glossary.noWarnings')}
        </span>
        <span className={styles['no-error-banner-count']}>
          {t('glossary.importCount', { count: importCount })}
        </span>
      </div>
    </div>
  );
};

/**
 * 1つの警告グループ（ラベル・件数・行リスト）
 */
const WarningGroup = ({ w }: { w: UploadWarning }) => {
  const { t } = useTranslation();
  const label = t(WARNING_TYPE_KEYS[w.type] as Parameters<typeof t>[0]);

  return (
    <div className={styles['warning-group']}>
      <div className={styles['warning-header']}>
        <img src={warning} alt="" className={styles['warning-icon']} />
        <span className={styles['warning-label']}>{label}</span>
        <span className={styles['warning-count']}>
          {t('glossary.warningCount', { num: w.rows.length })}
        </span>
      </div>
      <ul className={styles['warning-rows']}>
        {w.rows.map((r) => {
          const rowLabel = resolveWarningRowLabel(r, w.type, t);

          return (
            <li key={r.rowNum} className={styles['warning-row']}>
              <span className={styles['warning-row-num']}>
                {t('glossary.rowNumber', { row: r.rowNum })}
              </span>
              {/* title でホバー時に全文を表示。CSS ellipsis で折り返しを制御する。 */}
              <span className={styles['warning-row-content']} title={rowLabel}>
                {rowLabel}
              </span>
            </li>
          );
        })}
      </ul>
    </div>
  );
};

/**
 * 警告ありのとき表示するセクション。
 * 全行除外時は総件数付きのインポート不可メッセージを、
 * 一部除外時は「総件数中除外件数を除外しX件をインポート」メッセージを表示する。
 */
const UploadWarningSection = ({
  warnings,
  importCount,
  totalCount,
  isAllExcluded,
}: {
  warnings: UploadWarning[];
  importCount: number;
  /** パース対象となったデータ行の総数（ヘッダー行を除く） */
  totalCount: number;
  isAllExcluded: boolean;
}) => {
  const { t } = useTranslation();
  const excludedCount = totalCount - importCount;

  return (
    <div className={styles['result-section']}>
      <div className={styles['import-header']}>
        <span className={styles['import-header-text']}>
          {isAllExcluded
            ? t('glossary.allExcluded', { total: totalCount })
            : t('glossary.importCountPartial', {
                total: totalCount,
                excluded: excludedCount,
                count: importCount,
              })}
        </span>
      </div>
      <div className={styles['warning-section']}>
        {warnings.map((w) => (
          <WarningGroup key={w.type} w={w} />
        ))}
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// メインコンポーネント
// ---------------------------------------------------------------------------

/**
 * プロパティ
 */
type Props = {
  /** ダイアログの表示状態 */
  isOpen: boolean;
  /** ダイアログを閉じるコールバック */
  onClose: () => void;
  /**
   * CSV 登録完了時に呼ばれるコールバック。パース済みエントリを渡す。
   *
   * localStorageへの書き込み完了を待って結果({@link GlossarySaveResult})を返す
   * 非同期関数。`succeeded`以外の場合、ダイアログは閉じずに残す(PRISM-3110)。
   */
  onRegistered: (entries: GlossaryRow[]) => Promise<GlossarySaveResult>;
};

/**
 * 用語集 CSV アップロードダイアログ
 *
 * ドラッグ＆ドロップ・ファイル選択・パース結果プレビューを提供する。
 * ファイル操作・登録可否の判定ロジックは `useFileUpload` に委譲する。
 */
export const GlossaryUploadDialog = React.memo(
  ({ isOpen, onClose, onRegistered }: Props) => {
    const { t } = useTranslation();

    const {
      pendingFile,
      pendingEntries,
      importCount,
      pendingDataRowCount,
      canRegister,
      isAllExcluded,
      uploadWarnings,
      isReading,
      isDragging,
      fileTypeError,
      readError,
      parseError,
      headerError,
      fileInputRef,
      handleRemoveFile,
      handleDrop,
      handleDragOver,
      handleDragLeave,
      handleInputChange,
    } = useFileUpload();

    /** 登録処理中（localStorageへの書き込み完了待ち）か否か */
    const [isRegistering, setIsRegistering] = useState(false);
    /** 直近の登録処理の結果(未実施ならnull) */
    const [saveResult, setSaveResult] = useState<GlossarySaveResult | null>(
      null,
    );

    /**
     * マウント状態を追跡する。`onRegistered` の待機(最大10秒)中に
     * ログアウト等で画面遷移してアンマウントされた場合、その後の
     * setState呼び出し(handleClose含む)を防ぐために使う。
     */
    const isMountedRef = useRef(true);
    useEffect(
      () => () => {
        isMountedRef.current = false;
      },
      [],
    );

    /**
     * 選択ファイルが変わった（新しいファイル選択・削除）ら、
     * 別ファイルに対する古いエラー表示が残らないようにリセットする。
     */
    useEffect(() => {
      setSaveResult(null);
    }, [pendingFile]);

    /**
     * ダイアログを閉じると同時にファイル・パース結果をリセットする。
     * 登録処理中は誤って閉じられないようにする。
     */
    const handleClose = useCallback(() => {
      if (isRegistering) return;
      handleRemoveFile();
      setSaveResult(null);
      onClose();
    }, [isRegistering, handleRemoveFile, onClose]);

    /**
     * 登録ボタン押下時の処理。
     *
     * localStorageへの書き込み完了を待ち、成功以外の場合はダイアログを閉じずに残し、
     * 結果(容量超過・容量超過以外・タイムアウト)に応じた案内を、ダイアログ内に
     * インラインで表示する(PRISM-3110)。通知はこのダイアログ内で完結し、
     * アプリ全体向けのトースト等は出さない。
     *
     * `onRegistered` が例外を投げた場合も、待機状態のまま固まらないよう
     * catchして`timeout`扱いにし、スピナーを解除する。
     */
    const handleRegister = useCallback(async () => {
      if (!pendingFile || !canRegister) return;

      setIsRegistering(true);
      setSaveResult(null);

      let result: GlossarySaveResult;
      try {
        result = await onRegistered(pendingEntries);
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('用語集の登録処理で予期しないエラーが発生しました', error);
        // 原因不明の例外のため、特定の原因(容量超過・ブラウザ設定等)を決めつけない。
        // timeoutと同じ「時間を置いて再試行」という汎用的な案内にフォールバックする
        result = GLOSSARY_SAVE_RESULT.TIMEOUT;
      }

      // 待機中にログアウト等で画面遷移し、アンマウントされていたら何もしない
      if (!isMountedRef.current) return;

      setIsRegistering(false);

      if (result === GLOSSARY_SAVE_RESULT.SUCCEEDED) {
        handleClose();

        return;
      }
      setSaveResult(result);
    }, [pendingFile, canRegister, pendingEntries, onRegistered, handleClose]);

    return (
      <ModalDialog
        isOpen={isOpen}
        isHiddenCloseButton={false}
        onClickClose={handleClose}
        title={t('glossary.upload').toString()}
        maxWidth="500px"
      >
        <div className={styles['dialog-container']}>
          {/* 全体置換の注意 */}
          <div className={styles['dialog-warning']}>
            <img
              src={warning}
              alt=""
              className={styles['dialog-warning-icon']}
            />
            <p className={styles['dialog-warning-text']}>
              {t('glossary.replaceAllNotice')}
            </p>
          </div>

          <GlossaryLangPanel collapsible />

          {/* ファイル選択 / ドロップゾーン */}
          {/* isReading 中（onload 前）は pendingFile が null のためドロップゾーンを表示する */}
          {pendingFile ? (
            <div className={styles['file-card']}>
              <img
                src={descriptionIcon}
                alt=""
                className={styles['file-card-icon']}
              />
              <div className={styles['file-card-info']}>
                <span className={styles['file-card-name']}>
                  {pendingFile.name}
                </span>
              </div>
              <button
                type="button"
                className={styles['file-card-delete']}
                onClick={handleRemoveFile}
                aria-label="remove file"
                disabled={isRegistering}
              >
                <img src={close} alt="remove" />
              </button>
            </div>
          ) : (
            <div
              className={`${styles['drop-zone']} ${
                isDragging ? styles['drop-zone--dragging'] : ''
              } ${
                fileTypeError || readError || parseError || headerError
                  ? styles['drop-zone--error']
                  : ''
              }`}
              onDrop={handleDrop}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
            >
              <div className={styles['drop-icon']}>
                <img src={upload} alt="" className={styles['drop-icon-img']} />
              </div>
              <p className={styles['drop-label']}>{t('glossary.dropZone')}</p>
              <input
                ref={fileInputRef}
                id="glossary-upload-input"
                type="file"
                accept=".csv"
                onChange={handleInputChange}
                className={styles['file-input']}
              />
              <label
                htmlFor="glossary-upload-input"
                className={styles['file-label']}
              >
                {t('glossary.selectCsv')}
              </label>
              {isReading && (
                <p className={styles['file-reading']}>
                  {t('glossary.loading')}
                </p>
              )}
              {fileTypeError && (
                <p className={styles['file-type-error']}>
                  {t('glossary.fileTypeError')}
                </p>
              )}
              {readError && (
                <p className={styles['file-type-error']}>
                  {t('glossary.readError')}
                </p>
              )}
              {parseError && (
                <p className={styles['file-type-error']}>
                  {t('glossary.parseError')}
                </p>
              )}
              {headerError && (
                <p className={styles['file-type-error']}>
                  {t('glossary.headerError')}
                </p>
              )}
            </div>
          )}

          {/* パース結果 */}
          {pendingFile &&
            importCount !== null &&
            pendingDataRowCount !== null &&
            (uploadWarnings.length === 0 ? (
              <UploadResultBanner importCount={importCount} />
            ) : (
              <UploadWarningSection
                warnings={uploadWarnings}
                importCount={importCount}
                totalCount={pendingDataRowCount}
                isAllExcluded={isAllExcluded}
              />
            ))}

          {/* 保存結果メッセージ(localStorage書き込み失敗・タイムアウト時。PRISM-3110) */}
          {saveResult && saveResult !== GLOSSARY_SAVE_RESULT.SUCCEEDED && (
            <p
              className={styles['file-type-error']}
              data-testid={SAVE_RESULT_MESSAGE[saveResult].testId}
              // 非同期に挿入されるため、スクリーンリーダーに結果が伝わるようalertとして通知する
              role="alert"
            >
              {t(SAVE_RESULT_MESSAGE[saveResult].key)}
            </p>
          )}

          {/* アクションボタン */}
          <div className={styles['dialog-actions']}>
            <button
              type="button"
              className={styles['cancel-btn']}
              onClick={handleClose}
              disabled={isRegistering}
            >
              {t('glossary.cancel')}
            </button>
            {pendingFile && (
              <button
                type="button"
                className={styles['register-btn']}
                data-testid="upload-dialog-register-btn"
                onClick={handleRegister}
                disabled={!canRegister || isRegistering}
              >
                {t('glossary.register')}
              </button>
            )}
          </div>
        </div>
        {/* 登録処理中(localStorageへの書き込み完了待ち)はスピナーを表示する(PRISM-3110) */}
        <SpinnerOverlayView isOpen={isRegistering} />
      </ModalDialog>
    );
  },
);
